import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, Users, PenLine, Headphones, Package } from "lucide-react";
import { RESOURCE_LANDINGS } from "@/screens/resources/landings";
import { useResourceStatus, isVisible } from "@/lib/resources";
import { Split, Dawn, Arrow } from "@/components/hm/Basics";
import { BreathOrb, BreathSteps, BreathButton, useBreath } from "@/components/hm/BreathOrb";
import { Pricing } from "@/components/hm/Pricing";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/billing";
import { toast } from "sonner";

const PILLARS = [
  { id: "academia", icon: BookOpen, title: "Academia", p: [
    "La Academia es el espacio donde empiezas a entender lo que te está pasando por dentro.",
    "Aquí encontrarás contenido claro, profundo y accesible para comprender el vacío, la desconexión, la sensibilidad y este momento de reorganización interna que estás viviendo.",
    "Aprenderás a escucharte, a interpretar tus señales internas y a recuperar coherencia y dirección desde tu corazón, sin prisa y sin ruido.",
    "Un recorrido para volver a ti con claridad.",
  ] },
  { id: "comunidad", icon: Users, title: "Comunidad", p: [
    "Un espacio tranquilo donde compartir lo que sientes, preguntar y avanzar acompañado.",
    "Personas sensibles como tú, conversaciones honestas y un entorno seguro para explorar tu interior sin prisa.",
    "Un lugar donde tu corazón puede expresarse sin ruido.",
  ] },
  { id: "diario", icon: PenLine, title: "Diario", p: [
    "Tu espacio privado para escribir, ordenar lo que sientes y ver tu evolución.",
    "Solo para ti. Sin juicios. Sin exposición.",
    "Una herramienta para escucharte de verdad y darle espacio a tu corazón.",
  ] },
];

export const Bento = () => {
  const { user } = useAuth();
  return (
    <section className="sec welcome-mod" id="como" data-testid="landing-how">
      <div className="wrap">
        <div className="sec-head">
          <span className="eyebrow">Cómo funciona</span>
          <Split className="hm-h1" text="Cómo funciona Heartmony" data-testid="landing-how-title" />
          <p className="und-sub rv" style={{ margin: 0 }} data-testid="landing-how-subtitle">Tu recorrido tiene tres pilares</p>
        </div>
        <div className="pillars">
          {PILLARS.map((x, i) => (
            <article key={x.id} className={`pillar rv ${x.id === "academia" ? "pillar-main" : ""}`} style={{ "--d": `${i * 90}ms` }} data-testid={`landing-pillar-${x.id}`}>
              <span className="card-ico"><x.icon /></span>
              <h3><small>0{i + 1}</small>{x.title}</h3>
              {x.p.map((t) => <p key={t.slice(0, 24)}>{t}</p>)}
            </article>
          ))}
        </div>
        <div className="wm-cta rv">
          <Link to={user ? "/planes" : "/#planes"} className="btn btn-primary btn-hm wm-btn" data-testid="landing-how-plans-cta">Ver planes y acceder a los tres pilares <Arrow /></Link>
        </div>
      </div>
    </section>
  );
};

const RES_ICONS = { "meditacion-corazon-helicoidal": Headphones, "pack-iniciacion": Package, "resumen-la-energia-del-corazon": BookOpen };

export const Breathe = () => {
  const b = useBreath();
  const status = useResourceStatus();
  const shown = RESOURCE_LANDINGS.filter((r) => isVisible(status, r.slug));
  return (
    <section className="sec welcome-mod" id="respira" data-testid="landing-resources">
      <div className="wrap">
        <div className="sec-head">
          <span className="eyebrow">Recursos</span>
          <p className="lead rv" data-testid="landing-resources-intro">
            Aquí tienes tus primeros recursos para avanzar con claridad. Los ejercicios de respiración están abiertos. Para acceder a los demás recursos gratuitos, visita sus landings específicas y crea tu cuenta. Tu proceso es privado y solo tú verás lo que escribas.
          </p>
        </div>
        <div className="breathe wm-read" data-testid="resource-block-breath">
          <div>
            <span className="eyebrow">Respiración · 1 minuto</span>
            <Split className="hm-h1" style={{ marginTop: "1.2rem" }} text="Antes de seguir," serif="respira con nosotros." />
            <p className="lead rv" style={{ marginTop: "1.4rem" }}>Así son los ejercicios de Heartmony: cortos, guiados y para cualquier momento. Inhala 5, exhala 5. Respirar así te ayuda a calmar tu interior y a escuchar lo que tu corazón intenta decirte.</p>
            <BreathSteps phase={b.phase} />
            <BreathButton b={b} />
          </div>
          <BreathOrb b={b} />
        </div>
        <div className="res-grid" style={{ "--cols": shown.length }}>
          {shown.map((r, i) => {
            const Icon = RES_ICONS[r.slug];
            return (
              <article key={r.slug} className="card rv res-card" style={{ "--d": `${i * 90}ms` }} data-testid={`resource-block-${r.slug}`}>
                <div className="card-ico"><Icon /></div>
                <h3>{r.cardTitle || r.title}</h3>
                <p>{r.short}</p>
                <Link to={`/recursos/${r.slug}`} className="btn btn-ghost btn-sm wm-btn" data-testid={`resource-cta-${r.slug}`}>{r.cta} <Arrow /></Link>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const Quote = () => (
  <section className="quote">
    <div className="quote-glow" aria-hidden="true" />
    <div className="wrap">
      <blockquote className="split in">No estás roto. <em>Tu corazón te está llamando.</em></blockquote>
      <p className="rv">Y solo tienes que abrirle la puerta.</p>
    </div>
  </section>
);

export const LandingPricing = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(null);
  const choose = async (id, interval) => {
    if (!user) return nav("/registro");
    if (user.has_access && user.has_subscription) return nav("/planes");
    setLoading(id);
    try { await startCheckout(id, interval); } catch { toast.error("Algo no ha salido bien. Inténtalo otra vez."); setLoading(null); }
  };
  return (
    <section className="sec" id="planes" style={{ paddingTop: 0 }} data-testid="landing-plans">
      <div className="wrap">
        <div className="sec-head" style={{ marginInline: "auto", justifyItems: "center", textAlign: "center" }}>
          <span className="eyebrow">Planes</span>
          <Split className="hm-h1" text="Elige el tipo de acompañamiento" serif="que necesitas ahora." />
          <p className="lead rv">Empieza con el módulo de bienvenida y avanza a tu ritmo. Puedes cambiar de plan cuando lo necesites. Sin prisa. Sin presión.</p>
        </div>
        <Pricing onChoose={choose} loadingId={loading} currentPlan={user?.has_access ? user.plan : null} />
      </div>
    </section>
  );
};

const FAQ = [
  ["Heartmony es terapia", "No. Heartmony es un acompañamiento educativo y emocional para entender lo que sientes y recuperar claridad desde tu interior. No sustituye a un profesional de la salud mental. Si lo que vives te supera, busca apoyo profesional. Si es una urgencia, llama al 112."],
  ["Qué incluye el módulo gratis", "El módulo de bienvenida (lectura) y el ejercicio de respiración están abiertos para todos. La Meditación del Corazón y las 8 claves para recuperar la coherencia también son gratuitas, pero necesitas registrarte para acceder."],
  ["Cómo funcionan las sesiones", "Las sesiones grupales son semanales y online. La sesión individual es una al mes. Después del pago te acompañamos por email o WhatsApp para elegir la sesión que mejor encaje contigo."],
  ["Puedo cancelar cuando quiera", "Sí. Desde tu cuenta, en “Gestionar mi plan”. Mantienes el acceso hasta el final del periodo pagado."],
  ["Puedo pagar al año", "Sí. El pago anual equivale a dos meses gratis. También puedes cancelarlo desde tu cuenta."],
  ["Quién lee lo que escribo", "Tu diario solo lo ves tú. En la comunidad decides qué compartir y puedes hacerlo de forma anónima. Tu proceso es privado y respetado."],
];

export const Faq = () => (
  <section className="sec" id="faq" style={{ paddingTop: 0 }}>
    <div className="wrap faq">
      <div className="sticky-col">
        <span className="eyebrow">Preguntas</span>
        <Split className="hm-h1" style={{ marginTop: "1.2rem" }} text="Lo que quizá" serif="te estás preguntando." />
        <p className="lead rv" style={{ marginTop: "1.2rem" }}>Si tienes otra duda, escríbenos a contacto@heartmony.es.</p>
      </div>
      <div>
        {FAQ.map(([q, a], i) => (
          <details className="qa" key={q} open={i === 0} data-testid={`faq-item-${i}`}>
            <summary>{q}<i /></summary><p>{a}</p>
          </details>
        ))}
      </div>
    </div>
  </section>
);

export const FinalCta = () => (
  <section className="wrap">
    <div className="final">
      <Dawn />
      <span className="eyebrow">Tu primer paso</span>
      <Split className="hm-h1" style={{ marginTop: "1.2rem" }} text="En menos de diez minutos" serif="lo verás más claro." />
      <p className="lead rv">Empieza por el módulo de bienvenida. Es gratis y es tuyo.</p>
      <Link to="/modulo-bienvenida" className="btn btn-primary mag" data-testid="landing-cta-bottom">Empezar ahora <Arrow /></Link>
    </div>
  </section>
);
